/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Priority Lists", description = "Audience Priority Lists gives marketers control over the volume of marketing campaigns that a single user receives.", lastUpdatedDate = new Date(1673913600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Priority Lists",
        description: "Audience Priority Lists gives marketers control over the volume of marketing campaigns that a single user receives.",
        lastUpdatedDate: "Tue Jan 17 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }, {
          "value": "Can an audience be in multiple priority lists?",
          "depth": 1,
          "id": "can-an-audience-be-in-multiple-priority-lists"
        }, {
          "value": "Can a priority list have audiences created from different parent models?",
          "depth": 1,
          "id": "can-a-priority-list-have-audiences-created-from-different-parent-models"
        }, {
          "value": "Do Insight tools, like Breakdowns and Overlaps, reflect if an audience belongs to a priority list?",
          "depth": 1,
          "id": "do-insight-tools-like-breakdowns-and-overlaps-reflect-if-an-audience-belongs-to-a-priority-list"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      strong: "strong",
      ol: "ol",
      li: "li",
      img: "img",
      h3: "h3"
    }, _provideComponents(), props.components), {Video} = _components;
    if (!Video) _missingMdxReference("Video", true);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Priority Lists are part of the Advanced Customer Studio, available as an add-on to ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier plans"
          }), "."]
        })
      }), "\n", _jsx(Video, {
        source: "https://cdn.sanity.io/files/pwmfmi47/production/2dcda27bb1428a8bd83a644b78def8208ec9c09b.mp4",
        poster: "https://cdn.sanity.io/images/pwmfmi47/production/8711dc6ab54a16ea2e9fadaa923c81c84c486246-2480x1676.png",
        title: "Learn about Audience Priority Lists",
        duration: "3 min",
        publishedAt: "2022-11-15T08:00:00+08:00"
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Users often belong to multiple audiences based on buying behavior, brand affinity, demographic information, and more. You may have campaigns targeting each of these audiences, but you don't want to spam users by including them in every campaign targeting an audience they belong to."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Priority lists"
        }), " empower marketers to specify which audiences take priority over other audiences. With priority lists, when users belong to multiple audiences, Hightouch only syncs users to their highest priority audience. Prioritizing audience inclusion ensures that users only show up in the highest-value campaigns you run, saving money, and making campaigns more efficient."]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Before you begin creating priority lists, make sure you've set up ", _jsx(_components.a, {
            href: "/customer-studio/schema",
            children: "audiences"
          }), "."]
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Navigate to the ", _jsx(_components.a, {
              href: "https://app.hightouch.com/audiences",
              children: _jsx(_components.strong, {
                children: "Audiences"
              })
            }), " and select ", _jsx(_components.strong, {
              children: "Priority lists"
            }), " in the top right corner."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click the ", _jsx(_components.strong, {
              children: "Add priority list"
            }), " button in the top right corner."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Enter a ", _jsx(_components.strong, {
              children: "Name"
            }), " for your priority list and select ", _jsx(_components.a, {
              href: "/customer-studio/schema#the-parent-model-table",
              children: "a parent model"
            }), ". A priority list must consist of audiences that are derived from the same parent model."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "hightouch-audiences/priority-lists/create-priority-list-page.png",
              alt: "Create priority list page"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select the audiences to include in the priority list by clicking the ", _jsx(_components.strong, {
              children: "Add audiences"
            }), " button."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Reorder the priority of an audience by clicking and dragging on that audience. The higher up in the list an audience is, the higher priority it has."
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "hightouch-audiences/priority-lists/drag-and-drop.png",
              alt: "Drag and drop editor"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click the ", _jsx(_components.strong, {
              children: "Save"
            }), " button when the priority list has been configured."]
          }), "\n", _jsxs(_components.p, {
            children: ["You can update a priority list at any time from the priority list detail page. You can access a priority list's detail page by navigating to ", _jsx(_components.a, {
              href: "https://app.hightouch.com/customer-studio/priority-lists",
              children: _jsx(_components.strong, {
                children: "Priority Lists"
              })
            }), " and clicking on a priority list."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "can-an-audience-be-in-multiple-priority-lists",
        children: "Can an audience be in multiple priority lists?"
      }), "\n", _jsx(_components.p, {
        children: "No. One audience can only belong to one priority list."
      }), "\n", _jsx(_components.h3, {
        id: "can-a-priority-list-have-audiences-created-from-different-parent-models",
        children: "Can a priority list have audiences created from different parent models?"
      }), "\n", _jsxs(_components.p, {
        children: ["No. A single priority list must only contain audiences that share a common ", _jsx(_components.strong, {
          children: "Parent Model"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "do-insight-tools-like-breakdowns-and-overlaps-reflect-if-an-audience-belongs-to-a-priority-list",
        children: "Do Insight tools, like Breakdowns and Overlaps, reflect if an audience belongs to a priority list?"
      }), "\n", _jsxs(_components.p, {
        children: ["Yes-", _jsx(_components.a, {
          href: "/customer-studio/insights",
          children: _jsx(_components.strong, {
            children: "Audience Insights"
          })
        }), " take audience priority into account."]
      }), "\n", _jsxs(_components.p, {
        children: ["To learn more, read ", _jsxs(_components.a, {
          href: "/customer-studio/insights",
          children: ["the ", _jsx(_components.strong, {
            children: "Audience Insights"
          }), " docs"]
        }), " or navigate to the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/audiences",
          children: _jsx(_components.strong, {
            children: "Audiences"
          })
        }), " page and select an audience. The tools can be found under the ", _jsx(_components.strong, {
          children: "Overlap"
        }), " and ", _jsx(_components.strong, {
          children: "Breakdown"
        }), " tabs."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
